/**
 * Bündelt Klick-Handler + lokalen Fehlerzustand eines authentifizierten Datei-Downloads
 * (`api/basis.ts#ladeUndSpeichereDatei`) - ausgezogen aus vier fast wortgleichen Fassungen
 * (Auftrag "useDateiDownload-Hook einführen", 07.09.2026), die nach dem 401-Download-Fix
 * (Commit `1e384b97`, Fixrunde 2 `4259e6a0`/`4eafad27`) in `DokumenteTab.tsx`, `Checkliste.tsx`,
 * `ZahlungTab.tsx` und `DsvBelege.tsx` (dort das Vorbild) nebeneinander standen.
 *
 * Platzierung bewusst NEBEN `basis.ts` (gleicher Ordner) statt IN `basis.ts`: `basis.ts` beschreibt
 * sich in seinem Kopfkommentar ausdruecklich als "der einzige Ort, an dem das Frontend fetch gegen
 * die eigene API aufruft" und hat aktuell keinen React-Import - das soll so bleiben. Passend zum
 * bestehenden Muster eigenstaendiger Hook-Dateien (`state/useAnsicht.ts`,
 * `state/useEntprelltenAdresswert.ts`).
 *
 * `pfad` geht als `unknown` hinein und wird wie zuvor jede der vier `fileUrl(pfad)`-Fassungen mit
 * `String(pfad || "")` behandelt - die einzige echte Aufgabe dieser inzwischen toten Hilfsfunktionen,
 * jetzt hier statt viermal einzeln.
 *
 * Der Hook verwaltet seinen Fehlerzustand selbst (`DsvBelege.tsx`, die ihre Zeile inline behaelt).
 * `SicherungDetail.tsx` nutzt seit Fehleranzeige Paket 1, Task 9 (Ruling R-T8c) `useDateiDownloadMitStapel`:
 * seine Zeile am Ende des Detail-Panels entfaellt. Ein von aussen mitgegebenes `[fehler, setFehler]`-Paar (`eigenerFehlerZustand`, einst von
 * `DokumenteTab.tsx` gebraucht) gibt es seit Fehleranzeige Paket 1, Task 8 nicht mehr: der Reiter hat keine
 * Fehlerzeile mehr (siehe `useDateiDownloadMitStapel` unten), und das Paar hatte keinen Aufrufer.
 */
import { useState } from "react";
import { fehlertext } from "../lib/fehlertext.ts";
import { meldeFehler } from "../state/fehlerStapel.ts";
import { ladeUndSpeichereDatei } from "./basis.ts";

export interface DateiDownloadZugriff {
  fehler: string | null;
  herunterladen: (pfad: unknown, dateiname: string) => Promise<void>;
}

export function useDateiDownload(fehlermeldung: string): DateiDownloadZugriff {
  const [fehler, setFehler] = useState<string | null>(null);

  async function herunterladen(pfad: unknown, dateiname: string): Promise<void> {
    setFehler(null);
    try {
      await ladeUndSpeichereDatei(String(pfad || ""), dateiname);
    } catch (ursache) {
      // Inline bleibt inline (Spec R2): die Zeile gab es schon vorher. Neu ist nur der Text - der feste
      // Satz wird zum Kontext, die Ursache (404, Netz, ...) kommt aus `fehlertext()`. Ergibt der `null`
      // (Abbruch), bleibt die Zeile leer.
      setFehler(fehlertext(ursache, fehlermeldung));
    }
  }

  return { fehler, herunterladen };
}

/**
 * Die Fassung fuer Fehlerzeilen am Tab-Ende (Fehleranzeige Paket 1, Task 8, Ruling R-T5a, Spec R2):
 * `DokumenteTab`, `ZahlungTab` (laedt seit Ticket H selbst nichts mehr herunter), `IstZahlung` und `Checkliste`
 * hatten je eine `role="alert"`-Zeile ganz unten im Reiter, weit weg vom Knopf und ohne Scroll. Ihre
 * Aktionsfehler gehen an den
 * Fehlerstapel; die Zeile entfaellt. Deshalb gibt es hier keinen `fehler`-Zustand: wer sie nutzt, hat
 * nichts mehr zu zeichnen.
 *
 * Der feste Satz wird zum Kontext (Spec R1), die Ursache (404, Netz, ...) kommt aus `fehlertext()`;
 * ergibt der `null` (Abbruch, 401), meldet `meldeFehler` nichts. Gleiche Fehler zweier Klicks fasst der
 * Stapel zu einem Eintrag mit Zaehler zusammen.
 */
export function useDateiDownloadMitStapel(
  fehlermeldung: string,
): { herunterladen: (pfad: unknown, dateiname: string) => Promise<void> } {
  async function herunterladen(pfad: unknown, dateiname: string): Promise<void> {
    try {
      await ladeUndSpeichereDatei(String(pfad || ""), dateiname);
    } catch (ursache) {
      meldeFehler(ursache, fehlermeldung);
    }
  }

  return { herunterladen };
}
